// filtri/qualita.ts — v034: qualità automatica dei filtri (richiesta del proprietario: «sui cellulari vecchi i filtri
// si muovono lenti»). Il motore misura quanto gli costa ogni fotogramma (analisi del volto + shader + disegno) e
// quanti fotogrammi riesce davvero a fare; se il telefono non ce la fa scende di un livello, se ha margine per un po'
// risale. I livelli tolgono, in ordine: fotogrammi al secondo, risoluzione, un'analisi del volto su due, le animazioni
// più pesanti (i filtri le riducono come con «riduci movimento»: meno particelle, niente oscillazioni continue).
// La bocca resta sempre svuotata: quella non si semplifica mai.

export interface LivelloQualita {
  /** fotogrammi al secondo (al massimo quelli scelti dal pannello) */
  fps: number;
  /** lato più lungo del video in uscita, in pixel (al massimo quello del pannello) */
  lato: number;
  /** ogni quanti fotogrammi si analizza il volto (1 = sempre; 2 = uno sì e uno no, in mezzo si usa l'ultimo) */
  analisiOgni: number;
  /** i filtri disegnano come con «riduci movimento» */
  leggero: boolean;
  /** l'ombra morbida sotto gli oggetti (proporzioni.ts): costa, si toglie per prima */
  ombra: boolean;
}

export const LIVELLI: readonly LivelloQualita[] = [
  { fps: 30, lato: 960, analisiOgni: 1, leggero: false, ombra: true },
  { fps: 24, lato: 720, analisiOgni: 1, leggero: false, ombra: false },
  { fps: 20, lato: 560, analisiOgni: 2, leggero: false, ombra: false },
  { fps: 15, lato: 420, analisiOgni: 2, leggero: true, ombra: false },
];

/** Il livello reale: mai sopra i valori del pannello. */
export function livelloEffettivo(n: number, massimi: { fps: number; lato: number }): LivelloQualita {
  const l = LIVELLI[Math.max(0, Math.min(LIVELLI.length - 1, n))]!;
  return { ...l, fps: Math.min(l.fps, massimi.fps), lato: Math.min(l.lato, massimi.lato) };
}

/** Da dove partire: telefoni con pochi processori o poca memoria, o analisi sulla CPU (niente GPU), partono più in basso. */
export function livelloIniziale(indizi: { processori?: number; memoriaGb?: number; cpu?: boolean }): number {
  let n = 0;
  if ((indizi.processori ?? 8) <= 4) n++;
  if ((indizi.memoriaGb ?? 8) <= 2) n++;
  if (indizi.cpu) n++;
  return Math.min(n, LIVELLI.length - 1);
}

/** Quanto tempo deve durare la difficoltà (o il margine) prima di cambiare livello. */
const SCENDI_DOPO_MS = 1500;
const SALI_DOPO_MS = 10_000;
/** Dopo un cambio si aspetta prima di giudicare di nuovo (il telefono si deve assestare). */
const PAUSA_DOPO_CAMBIO_MS = 3000;
/** Frazione del tempo di un fotogramma oltre la quale «non ce la fa», e sotto la quale «ha margine». */
const TROPPO = 0.85;
const MARGINE = 0.4;
/** Fotogrammi fatti davvero / fotogrammi chiesti, sotto questo valore «non ce la fa». */
const RITMO_MINIMO = 0.75;
/** Peso di ogni misura nella media (più alto = reagisce prima). */
const PESO = 0.1;
/**
 * v046: al livello più basso non si può più scendere: se il telefono non ce la fa ancora per questo tempo, il filtro è
 * troppo pesante per lui (il motore lo dice una volta con `suAffanno` e la partita passa a un filtro leggero).
 */
export const AFFANNO_DOPO_MS = 4000;

/**
 * Decide il livello a partire dalle misure. Si chiama a ogni fotogramma con quanto è costato (ms) e l'ora; restituisce
 * il livello nuovo quando cambia, altrimenti null.
 */
export class QualitaAutomatica {
  livello: number;
  private costoMedio = 0;
  private fatti: number[] = [];
  private difficileDa: number | null = null;
  private comodoDa: number | null = null;
  private fermoFino = 0;
  /** v046: da quando non ce la fa anche al livello più basso (null = ce la fa) */
  private affannoDa: number | null = null;
  /** v046: misure fatte con la qualità attiva (per sapere se il livello raggiunto vuol dire qualcosa) */
  misure = 0;
  private readonly massimi: { fps: number; lato: number };
  private readonly attiva: boolean;

  constructor(o: { iniziale: number; massimi: { fps: number; lato: number }; attiva: boolean }) {
    this.livello = o.attiva ? o.iniziale : 0;
    this.massimi = o.massimi;
    this.attiva = o.attiva;
  }

  get attuale(): LivelloQualita {
    return livelloEffettivo(this.livello, this.massimi);
  }

  /** La media del costo di un fotogramma (ms), per il pannello dei tester. */
  get costo(): number {
    return this.costoMedio;
  }

  misura(costoMs: number, oraMs: number): number | null {
    if (!this.attiva) return null;
    this.misure++;
    this.costoMedio = this.costoMedio ? this.costoMedio + PESO * (costoMs - this.costoMedio) : costoMs;
    this.fatti.push(oraMs);
    while (this.fatti.length && this.fatti[0]! < oraMs - 2000) this.fatti.shift();
    if (oraMs < this.fermoFino) return null;
    const l = this.attuale;
    const intervallo = 1000 / l.fps;
    // fotogrammi al secondo davvero fatti negli ultimi 2 secondi (solo quando la finestra è piena)
    const ritmo = this.fatti.length > 1 ? ((this.fatti.length - 1) * 1000) / Math.max(1, oraMs - this.fatti[0]!) : l.fps;
    const finestraPiena = this.fatti.length > 1 && oraMs - this.fatti[0]! > 1500;
    const difficile = this.costoMedio > intervallo * TROPPO || (finestraPiena && ritmo < l.fps * RITMO_MINIMO);
    const sopra = this.livello > 0 ? livelloEffettivo(this.livello - 1, this.massimi) : null;
    const comodo = sopra !== null && this.costoMedio < (1000 / sopra.fps) * MARGINE && (!finestraPiena || ritmo >= l.fps * 0.95);

    this.difficileDa = difficile ? (this.difficileDa ?? oraMs) : null;
    this.affannoDa = difficile && this.livello >= LIVELLI.length - 1 ? (this.affannoDa ?? oraMs) : null;
    this.comodoDa = comodo ? (this.comodoDa ?? oraMs) : null;
    if (this.difficileDa !== null && oraMs - this.difficileDa >= SCENDI_DOPO_MS && this.livello < LIVELLI.length - 1) return this.cambia(this.livello + 1, oraMs);
    if (this.comodoDa !== null && oraMs - this.comodoDa >= SALI_DOPO_MS) return this.cambia(this.livello - 1, oraMs);
    return null;
  }

  /** v046: da quanti ms il telefono non ce la fa nemmeno al livello più basso (0 = ce la fa, o non è al minimo). */
  affannoMs(oraMs: number): number {
    return this.affannoDa === null ? 0 : Math.max(0, oraMs - this.affannoDa);
  }

  /**
   * v046: è cambiato il filtro: il costo di prima non dice più niente. Il livello resta (il telefono è lo stesso),
   * le misure ricominciano dopo una pausa.
   */
  ricomincia(oraMs: number): void {
    this.costoMedio = 0;
    this.fatti = [];
    this.difficileDa = null;
    this.comodoDa = null;
    this.affannoDa = null;
    this.fermoFino = oraMs + PAUSA_DOPO_CAMBIO_MS;
  }

  private cambia(n: number, oraMs: number): number {
    this.livello = n;
    this.difficileDa = null;
    this.comodoDa = null;
    this.affannoDa = null;
    this.fatti = [];
    this.fermoFino = oraMs + PAUSA_DOPO_CAMBIO_MS;
    return n;
  }
}
